import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Big } from "big.js";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCase, useCaseSubmit } from "../../context/CaseContext";
import { useCurrency } from "../../context/CurrencyContext";
import { CostType, Stage } from "../../shared/CaseConfiguration";
import { openEstimates, withSpentAt } from "../../shared/costChanges";
import { Budget } from "../../shared/CostModel";
import { Judgment } from "../../shared/judgment";
import {
  AwardRow,
  awardRows,
  decisionIds,
  decisionsOf,
} from "../../shared/judgmentDecisions";
import {
  endDateRange,
  IsoDate,
  showDate,
  withEnded,
  withPassedOver,
} from "../../shared/timeline";
import { TABULAR_NUMERALS } from "../general/dataAtoms/shared";
import { SectionLabel } from "../general/SectionLabel";
import { SegmentedControl } from "../general/SegmentedControl";
import { amountOf, PartySpending, useStageSpending } from "./CloseStageDialog";
import { Decision, JudgmentClaim } from "./JudgmentClaims";
import {
  draftOf,
  estimatesOf,
  orderOf,
  recoveryOf,
  RecoveryDraft,
} from "./judgmentDraft";
import { RecoveryOrderTable } from "./RecoveryOrderTable";
import { useJudgmentPreview } from "./useJudgmentPreview";
import { AsExpectedButton } from "./AsExpectedButton";

const button = {
  textTransform: "none",
  fontWeight: 600,
  whiteSpace: "nowrap",
} as const;
const amount = { ...TABULAR_NUMERALS, whiteSpace: "nowrap" } as const;

const awardKey = ({
  issueId,
  damageId,
}: Pick<AwardRow, "issueId" | "damageId">) => `${issueId}:${damageId}`;

/**
 * Closes Trial with the judgment: the date, what was spent, what becomes of
 * the estimates still open, and what the court decided and awarded. The user
 * decides every claim down to its lowest issues; only then does the case's
 * matching outcome give the amounts to start from, and the cost recoveries of
 * the winners appear. With `editing`, the user comes back to a judgment
 * already made, with Trial closed, typically to enter an assessment.
 */
export const CloseTrialDialog = ({
  today,
  onClose,
  editing = false,
}: {
  today: IsoDate;
  onClose: () => void;
  editing?: boolean;
}) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.timeline.judgment",
  });
  const { t: tRoot } = useTranslation();
  const { currency } = useCurrency();
  const money = (value: Big | number) =>
    currency.format(typeof value === "number" ? value : value.toNumber());
  const caseContext = useCase();
  const c = caseContext.getCase();
  const submit = useCaseSubmit();
  const judgment = c.judgment;
  const stage = Stage.Trial;
  const stageName = tRoot(`home.stages.${Stage[stage]}`);
  const spending = useStageSpending(stage);

  const [date, setDate] = useState<IsoDate>(
    (editing && c.timeline?.stages?.[stage]?.ended) || "",
  );
  const [decided, setDecided] = useState<{ [id: string]: Decision }>(() =>
    Object.fromEntries([
      ...(judgment?.scenario.won ?? []).map((id) => [id, "won"]),
      ...(judgment?.scenario.lost ?? []).map((id) => [id, "lost"]),
    ]),
  );
  const [awards, setAwards] = useState<{ [key: string]: string }>(() =>
    Object.fromEntries(
      (judgment?.awards ?? []).map((award) => [
        awardKey(award),
        award.amount.toString(),
      ]),
    ),
  );
  const [interest, setInterest] = useState<{ [claimId: string]: string }>(() =>
    Object.fromEntries(
      (judgment?.interest ?? []).map(({ issueId, amount }) => [
        issueId,
        amount.toString(),
      ]),
    ),
  );
  const [estimateChoices, setEstimateChoices] = useState<{
    [key: string]: "spent" | "notSpent";
  }>({});
  const [drafts, setDrafts] = useState<{ [partyId: string]: RecoveryDraft }>(
    {},
  );

  const estimates = editing
    ? []
    : spending.parties.flatMap(({ partyId, name, budget, locked }) =>
        openEstimates(budget, locked, stage).map((estimate) => ({
          ...estimate,
          partyId,
          name,
          key: `${partyId}:${estimate.stage}:${estimate.type}`,
        })),
      );

  const budgetsNow = useMemo<Budget[]>(() => {
    const byParty = new Map((c.budgets ?? []).map((b) => [b.partyId, b]));
    if (editing) return [...byParty.values()];
    for (const budget of spending.changedBudgets())
      byParty.set(budget.partyId, budget);
    for (const { key, partyId, stage: at, type, planned } of estimates) {
      if (estimateChoices[key] !== "spent") continue;
      const locked = spending.parties.find(
        (party) => party.partyId === partyId,
      )?.locked;
      const budget = byParty.get(partyId);
      if (budget && locked)
        byParty.set(partyId, withSpentAt(budget, locked, at, type, planned));
    }
    return [...byParty.values()];
    // what the dialog has entered of the costs decides them
  }, [
    c.budgets,
    editing,
    estimateChoices,
    JSON.stringify(spending.changedBudgets()),
  ]);

  const decisions = useMemo(() => decisionsOf(c), [c]);
  const ids = useMemo(() => decisionIds(decisions), [decisions]);
  const matched = ids.every((id) => decided[id] !== undefined);
  const scenario = useMemo(
    () =>
      matched
        ? {
            won: ids.filter((id) => decided[id] === "won"),
            lost: ids.filter((id) => decided[id] === "lost"),
          }
        : undefined,
    [matched, ids, decided],
  );
  const { preview, loading, failed } = useJudgmentPreview(
    c.id,
    scenario,
    budgetsNow,
  );
  const won = useMemo(
    () =>
      new Set(
        Object.entries(decided).flatMap(([id, decision]) =>
          decision === "won" ? [id] : [],
        ),
      ),
    [decided],
  );
  const rows = useMemo(() => awardRows(c, won), [c, won]);
  const awardText = (row: AwardRow) =>
    awards[awardKey(row)] ?? (matched ? row.expected.toString() : "");

  // a winner's recovery starts at the estimates of the case, or as ordered
  useEffect(() => {
    if (preview === undefined) return;
    setDrafts((was) =>
      Object.fromEntries(
        preview.recoverable.map((recoverable) => [
          recoverable.recipientId,
          was[recoverable.recipientId] ??
            draftOf(
              recoverable,
              c.parties?.find(({ id }) => id === recoverable.recipientId),
              judgment?.recoveries[recoverable.recipientId],
            ),
        ]),
      ),
    );
    // the drafts follow what the preview says only
  }, [preview]);

  const partyName = (id: string) =>
    c.parties?.find((party) => party.id === id)?.name ?? id;
  const range = endDateRange(c.timeline, caseContext.getStages(), stage, today);
  const tooEarly = date !== "" && range.min !== undefined && date < range.min;
  const validDate = date !== "" && !tooEarly && date <= range.max;
  const validAwards =
    matched &&
    rows.every((row) => amountOf(awardText(row)) !== undefined) &&
    Object.values(interest).every((text) => amountOf(text) !== undefined);
  const recoverable = preview?.recoverable ?? [];
  const recoveriesReady =
    preview !== undefined &&
    recoverable.every(
      ({ recipientId }) =>
        drafts[recipientId] !== undefined &&
        drafts[recipientId].draft === undefined,
    );
  const missing = [
    !validDate && t("parts.date"),
    !editing && !spending.validAmounts && t("parts.costs"),
    estimates.some(({ key }) => !estimateChoices[key]) && t("parts.estimates"),
    !matched && t("parts.decisions"),
    matched && !validAwards && t("parts.awards"),
    matched && !loading && !recoveriesReady && t("parts.recoveries"),
  ].filter((part): part is string => !!part);

  const close = () => {
    const judged: Judgment = {
      scenario: scenario!,
      awards: rows.map((row) => ({
        issueId: row.issueId,
        damageId: row.damageId,
        amount: amountOf(awardText(row))!.toNumber(),
      })),
      interest: Object.entries(interest).map(([issueId, text]) => ({
        issueId,
        amount: amountOf(text)!.toNumber(),
      })),
      recoveries: Object.fromEntries(
        recoverable.map(({ recipientId }) => [
          recipientId,
          orderOf(drafts[recipientId]),
        ]),
      ),
    };
    const timeline = withPassedOver(
      withEnded(c.timeline, stage, date),
      Stage.Appeals,
    );
    return Promise.resolve(
      submit(
        {
          timeline: JSON.stringify(timeline),
          budgets: JSON.stringify(budgetsNow),
          judgment: JSON.stringify(judged),
        },
        { method: "put" },
      ),
    ).then(onClose);
  };

  const claimsOfClient = (partyId: string) => {
    const of = (row: { claimId: string }, value: Big) => {
      const claim = c.claims?.find(({ id }) => id === row.claimId);
      if (claim?.claimant.id === partyId)
        return { forIt: value, against: new Big(0) };
      return claim?.defendants?.some(({ id }) => id === partyId)
        ? { forIt: new Big(0), against: value }
        : { forIt: new Big(0), against: new Big(0) };
    };
    const lines = [
      ...rows.map((row) => of(row, amountOf(awardText(row)) ?? new Big(0))),
      ...Object.entries(interest).map(([claimId, text]) =>
        of({ claimId }, amountOf(text) ?? new Big(0)),
      ),
    ];
    const forIt = lines.reduce((sum, line) => sum.add(line.forIt), new Big(0));
    const against = lines.reduce(
      (sum, line) => sum.add(line.against),
      new Big(0),
    );
    const costs = recoverable.reduce((sum, order) => {
      const draft = drafts[order.recipientId];
      if (draft === undefined) return sum;
      const recovery = recoveryOf(order, draft);
      if (order.recipientId === partyId) return sum.add(recovery);
      return order.payerIds.includes(partyId) ? sum.minus(recovery) : sum;
    }, new Big(0));
    return { forIt, against, costs, net: forIt.minus(against).add(costs) };
  };

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{t(editing ? "editTitle" : "title")}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>{t("description")}</DialogContentText>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <TextField
            type="date"
            size="small"
            required
            label={t("date")}
            value={date}
            onChange={(event) => setDate(event.target.value)}
            error={date !== "" && !validDate}
            helperText={
              date === "" || validDate
                ? undefined
                : tooEarly
                  ? tRoot("case.timeline.notBefore", {
                      date: showDate(range.min!),
                    })
                  : tRoot("case.timeline.notAfter", {
                      date: showDate(range.max),
                    })
            }
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { min: range.min, max: range.max },
            }}
            sx={{ width: 220 }}
          />
          <Button variant="outlined" sx={button} onClick={() => setDate(today)}>
            {t("today")}
          </Button>
          <AsExpectedButton stage={stage} today={today} onPick={setDate} />
        </Stack>

        {!editing &&
          spending.parties.map((party) => (
            <PartySpending
              key={party.partyId}
              party={party}
              stageName={stageName}
              final
              textOf={(type) => spending.textOf(party.partyId, type)}
              set={(type, value) => spending.set(party.partyId, type, value)}
            />
          ))}

        {estimates.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <SectionLabel component="h3">{t("estimates.title")}</SectionLabel>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mt: 0.5 }}
            >
              {t("estimates.description")}
            </Typography>
            <Box sx={{ overflowX: "auto", mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("estimates.column.party")}</TableCell>
                    <TableCell>{t("estimates.column.stage")}</TableCell>
                    <TableCell>{t("estimates.column.type")}</TableCell>
                    <TableCell align="right">
                      {t("estimates.column.estimated")}
                    </TableCell>
                    <TableCell>{t("estimates.column.outcome")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {estimates.map(
                    ({ key, name, stage: at, type, estimated }) => (
                      <TableRow key={key}>
                        <TableCell>{name}</TableCell>
                        <TableCell>
                          {tRoot(`home.stages.${Stage[at]}`)}
                        </TableCell>
                        <TableCell>
                          {t(`recoveries.costTypes.${type as CostType}`)}
                        </TableCell>
                        <TableCell align="right" sx={amount}>
                          {money(estimated)}
                        </TableCell>
                        <TableCell>
                          <SegmentedControl<"spent" | "notSpent" | "">
                            ariaLabel={t("estimates.column.outcome")}
                            value={estimateChoices[key] ?? ""}
                            options={[
                              { value: "spent", label: t("estimates.spent") },
                              {
                                value: "notSpent",
                                label: t("estimates.notSpent"),
                              },
                            ]}
                            onChange={(choice) =>
                              choice !== "" &&
                              setEstimateChoices((was) => ({
                                ...was,
                                [key]: choice,
                              }))
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ),
                  )}
                </TableBody>
              </Table>
            </Box>
          </Box>
        )}

        <Box sx={{ mt: 3 }}>
          <SectionLabel component="h3">{t("claims.title")}</SectionLabel>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
            {t("claims.description")}
          </Typography>
          {decisions.map((claimDecisions) => (
            <JudgmentClaim
              key={claimDecisions.claim.id}
              decisions={claimDecisions}
              decided={decided}
              onDecide={(id, decision) =>
                setDecided((was) => ({ ...was, [id]: decision }))
              }
              rows={rows.filter(
                ({ claimId }) => claimId === claimDecisions.claim.id,
              )}
              matched={matched}
              awardOf={awardText}
              setAward={(row, value) =>
                setAwards((was) => ({ ...was, [awardKey(row)]: value }))
              }
              interest={interest[claimDecisions.claim.id]}
              setInterest={(value) =>
                setInterest((was) => ({
                  ...was,
                  [claimDecisions.claim.id]: value,
                }))
              }
            />
          ))}
        </Box>

        {matched && (
          <>
            {failed && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {t("outcome.failed")}
              </Alert>
            )}
            {loading && (
              <Typography
                variant="body2"
                sx={{ mt: 2, color: "text.secondary" }}
              >
                {t("outcome.loading")}
              </Typography>
            )}
            {preview && (
              <>
                <Stack
                  direction="row"
                  useFlexGap
                  sx={{
                    flexWrap: "wrap",
                    gap: 2,
                    alignItems: "center",
                    mt: 2,
                    px: 2,
                    py: 1.5,
                    borderRadius: 2,
                    bgcolor: "primary.dark",
                    color: "primary.contrastText",
                  }}
                >
                  <Typography
                    sx={{ fontSize: 30, fontWeight: 700, ...TABULAR_NUMERALS }}
                  >
                    {new Big(preview.probability).mul(100).toFixed(1)}%
                  </Typography>
                  <Box>
                    <Typography sx={{ fontSize: 15, fontWeight: 600 }}>
                      {t("outcome.chance")}
                    </Typography>
                    <Typography sx={{ fontSize: 13, opacity: 0.85 }}>
                      {t(
                        preview.probability > 0
                          ? "outcome.hint"
                          : "outcome.ruledOut",
                      )}
                    </Typography>
                  </Box>
                </Stack>

                <Box sx={{ mt: 3 }}>
                  <SectionLabel component="h3">
                    {t("recoveries.title")}
                  </SectionLabel>
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", mt: 0.5 }}
                  >
                    {t(
                      recoverable.length > 0
                        ? "recoveries.description"
                        : "recoveries.none",
                    )}
                  </Typography>
                  {recoverable.map((order) =>
                    drafts[order.recipientId] ? (
                      <RecoveryOrderTable
                        key={order.recipientId}
                        name={partyName(order.recipientId)}
                        payers={order.payerIds
                          .map(partyName)
                          .join(t("recoveries.and"))}
                        recoverable={order}
                        estimates={estimatesOf(
                          c.parties?.find(({ id }) => id === order.recipientId),
                        )}
                        draft={drafts[order.recipientId]}
                        onChange={(draft) =>
                          setDrafts((was) => ({
                            ...was,
                            [order.recipientId]: draft,
                          }))
                        }
                      />
                    ) : null,
                  )}
                </Box>

                {spending.parties.map(({ partyId, name }) => {
                  const figures = claimsOfClient(partyId);
                  const tiles: [string, Big, string?][] = [
                    [t("summary.against"), figures.against],
                    [t("summary.forIt"), figures.forIt],
                    [t("summary.recoveries"), figures.costs],
                    [t("summary.net"), figures.net, t("summary.netHint")],
                  ];
                  return (
                    <Box
                      key={partyId}
                      sx={{
                        mt: 2.5,
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: "action.hover",
                      }}
                    >
                      <SectionLabel component="h3">
                        {t("summary.title", { name })}
                      </SectionLabel>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fit, minmax(160px, 1fr))",
                          gap: 2,
                          mt: 1,
                        }}
                      >
                        {tiles.map(([label, value, hint]) => (
                          <Box key={label}>
                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary" }}
                            >
                              {label}
                            </Typography>
                            <Typography
                              sx={{ fontSize: 18, fontWeight: 600, ...amount }}
                            >
                              {money(value)}
                            </Typography>
                            {hint && (
                              <Typography
                                variant="caption"
                                sx={{ color: "text.secondary" }}
                              >
                                {hint}
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  );
                })}
              </>
            )}
          </>
        )}
        {!editing && (
          <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
            {t("passedOver")}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        {missing.length > 0 && (
          <Typography
            variant="body2"
            sx={{ mr: "auto", color: "text.secondary" }}
          >
            {t("missing", { parts: missing.join(", ") })}
          </Typography>
        )}
        <Button sx={button} onClick={onClose}>
          {t("cancel")}
        </Button>
        <Button
          variant="contained"
          sx={button}
          disabled={missing.length > 0 || loading || !preview}
          onClick={close}
        >
          {t(editing ? "save" : "submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
